import { useQuery } from '@tanstack/react-query';
import qrcode from 'qrcode-generator';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as api from './api.ts';
import { Button, Card, Finding, Label, Mono } from './ui.tsx';

/**
 * The QR codes a building prints (docs/qr-codes.md).
 *
 * Two codes, each with one job, and a printable A4 poster for each:
 *
 * - **Follow this place** — the follow door. Anyone who scans it can ask to be
 *   told if this building runs an evacuation or a lockdown. Never offered for
 *   a sensitive building (the server returns no link for one).
 * - **Visitor sign-in** — the kiosk. The code is the credential; a visitor
 *   signs in on their own phone and the sign-in ends by itself.
 *
 * Every URL comes from `GET /orgs/:orgId/sites/:siteId/qr-links`; this file
 * never builds a link (guardrail G20). The codes are drawn here, in the
 * browser — nothing is sent anywhere to make them.
 */

export type QrKind = 'follow' | 'kiosk';

/** The words each code carries, on screen and on the poster. */
export const QR_COPY: Record<
  QrKind,
  { title: string; purpose: string; posterTitle: string; posterSteps: (site: string) => string[] }
> = {
  follow: {
    title: 'Follow this place',
    purpose:
      'For the evacuation diagram, the front window or the newsletter. Anyone who scans it can ask to be told if this building runs an evacuation or a lockdown, and when it is over. Nothing to install.',
    posterTitle: 'Be told if this building has an emergency',
    posterSteps: (site) => [
      'Point your phone camera at this code.',
      'Type your email address.',
      `We'll tell you if ${site} runs an evacuation or a lockdown — and again when it's over.`,
    ],
  },
  kiosk: {
    title: 'Visitor sign-in',
    purpose:
      'For the front desk or wherever people arrive. A visitor scans it, gives their name and how long they are staying, and is counted if there is an evacuation. The sign-in ends by itself.',
    posterTitle: 'Visiting? Sign in here',
    posterSteps: (site) => [
      'Point your phone camera at this code.',
      'Type your name and how long you are staying.',
      `If ${site} is evacuated, you will be counted. Your sign-in ends by itself when you leave.`,
    ],
  },
};

/** The dark modules of a QR code as one SVG path. Error correction M. */
export function qrPath(text: string): { size: number; d: string } {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  let d = '';
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      if (qr.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`;
    }
  }
  return { size: n, d };
}

/** A QR code, drawn as SVG with a four-module quiet zone. */
export function QrSvg({ text, label, px = 176 }: { text: string; label: string; px?: number }) {
  const { size, d } = qrPath(text);
  const box = size + 8;
  return (
    <svg
      role="img"
      aria-label={label}
      width={px}
      height={px}
      viewBox={`-4 -4 ${box} ${box}`}
      shapeRendering="crispEdges"
    >
      <rect x={-4} y={-4} width={box} height={box} fill="#fff" />
      <path d={d} fill="#000" />
    </svg>
  );
}

/** The code as a standalone SVG file, for "Save image". */
function svgFile(text: string): string {
  const { size, d } = qrPath(text);
  const box = size + 8;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${box * 10}" height="${box * 10}" viewBox="-4 -4 ${box} ${box}" shape-rendering="crispEdges"><rect x="-4" y="-4" width="${box}" height="${box}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
}

function saveImage(text: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([svgFile(text)], { type: 'image/svg+xml' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function slug(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'building'
  );
}

/**
 * The A4 poster, rendered only while printing. The browser's own print dialog
 * saves it as a PDF — the same "the browser is the renderer" choice the
 * signed report makes.
 */
function Poster({ kind, url, site }: { kind: QrKind; url: string; site: string }) {
  const copy = QR_COPY[kind];
  return (
    <div className="qr-print" aria-hidden="true">
      <div className="qr-print-sheet">
        <p className="qr-print-site">{site}</p>
        <h1 className="qr-print-title">{copy.posterTitle}</h1>
        <div className="qr-print-code">
          <QrSvg text={url} label={copy.title} px={420} />
        </div>
        <ol className="qr-print-steps">
          {copy.posterSteps(site).map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <p className="qr-print-url">{url}</p>
        <p className="qr-print-brand">EVAC by Duress</p>
      </div>
    </div>
  );
}

function QrBlock({
  kind,
  url,
  site,
  onPrint,
}: {
  kind: QrKind;
  url: string;
  site: string;
  onPrint: () => void;
}) {
  const copy = QR_COPY[kind];
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-wrap gap-4 border-t border-[var(--line)] pt-4 first:border-t-0 first:pt-0">
      <div className="shrink-0 rounded-[var(--radius-1)] border border-[var(--line)] bg-white p-1">
        <QrSvg text={url} label={`QR code: ${copy.title}`} />
      </div>
      <div className="min-w-[240px] flex-1">
        <div className="mb-1">
          <Label>{copy.title}</Label>
        </div>
        <p className="mb-2 max-w-prose text-[13px] text-[var(--ink-2)]">{copy.purpose}</p>
        <Mono>{url}</Mono>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="primary" onClick={onPrint} label={`Print the ${copy.title} poster`}>
            Print A4 poster
          </Button>
          <Button
            onClick={() => saveImage(url, `${slug(site)}-${kind}-qr.svg`)}
            label={`Save the ${copy.title} code as an image`}
          >
            Save image
          </Button>
          <Button
            variant="quiet"
            label={`Copy the ${copy.title} link`}
            onClick={() => {
              void navigator.clipboard?.writeText(url).then(
                () => setCopied(true),
                () => setCopied(false),
              );
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The building's QR codes, each with its purpose, a poster and an image. */
export function QrCodes({
  orgId,
  siteId,
  followers,
}: {
  orgId: string;
  siteId: string;
  /** How many people follow this building so far, when known. */
  followers?: number;
}) {
  const links = useQuery({
    queryKey: ['qr-links', orgId, siteId],
    queryFn: () => api.getQrLinks(orgId, siteId),
  });
  const [printing, setPrinting] = useState<QrKind | null>(null);

  useEffect(() => {
    if (!printing) return;
    document.body.classList.add('qr-printing');
    const done = () => setPrinting(null);
    window.addEventListener('afterprint', done);
    // Next frame, so the poster is in the document before the dialog opens.
    const t = window.setTimeout(() => window.print(), 50);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('afterprint', done);
      document.body.classList.remove('qr-printing');
    };
  }, [printing]);

  if (links.isError) {
    return (
      <Card title="QR codes" className="mb-6">
        <Finding>{api.errorMessage(links.error) ?? 'The QR codes could not be loaded.'}</Finding>
      </Card>
    );
  }
  if (!links.data) return null;
  const { siteName, follow, followRefusal, kiosk } = links.data;
  const printUrl = printing === 'follow' ? follow?.url : printing === 'kiosk' ? kiosk.url : null;

  return (
    <Card title="QR codes" className="mb-6">
      <div className="space-y-4">
        {follow ? (
          <QrBlock
            kind="follow"
            url={follow.url}
            site={siteName}
            onPrint={() => setPrinting('follow')}
          />
        ) : followRefusal ? (
          <p className="max-w-prose text-[13px] text-[var(--ink-2)]">{followRefusal}</p>
        ) : null}
        {follow && followers !== undefined ? (
          <p className="text-[13px] text-[var(--ink-2)]">{followers} following so far.</p>
        ) : null}
        <QrBlock
          kind="kiosk"
          url={kiosk.url}
          site={siteName}
          onPrint={() => setPrinting('kiosk')}
        />
      </div>
      {printing && printUrl
        ? createPortal(<Poster kind={printing} url={printUrl} site={siteName} />, document.body)
        : null}
    </Card>
  );
}
